import React, { useCallback } from "react";
import { TurnrModalContainer, TurnrModalContent, TurnrModalFooter, TurnrModalTitle, useModalServiceTriggers } from "~/components/modals/TurnrModal.jsx";
import { useIdList } from "~/hooks";
import { ENTITY_ACTION_TYPE, ENTITY_SCOPE, ENTITY_TYPE, WAREHOUSE_ITEM_ACTION, WAREHOUSE_ITEM_STATUS } from "~/constants";
import AvatarAndItemName from "~/components/items/AvatarAndItemName.jsx";
import { WarehouseComplaintInfo } from "~/components/Complaint.jsx";
import AcceptDenyButtons from "~/components/AcceptDenyButtons.jsx";
import { TurnrTableBody, TurnrTableCell, TurnrTableHead, TurnrTableHeaderCell, TurnrTableHeaderRow, TurnrTableRow, TurnrTableSimple } from "~/components/table/TurnrTableSmart.jsx";
import { fetchEntity, performEntityAction } from "~/services";
import WarehouseItemActionStatusChip from "~/components/chips/statusChips/WarehouseItemActionStatusChip.jsx";
import { serviceIsLoading, useServiceHook } from "rylak-common";
import { TurnrCard } from "~/components/card/TurnrCard.jsx";
import SkeletonLoaderCardSlim from "~/components/loading/skeletons/SkeletonLoaderCardSlim.jsx";

export default function HandleWarehouseComplaintModal(props) {
    const { warehouseReportId, appProps, onClose, onSubmit, closeOnSubmit, title } = props;

    let { list: warehouseItemUpdates, getItem, removeItem, updateItem } = useIdList();

    let { serviceData: warehouseReturnService } = useServiceHook({
        appProps,
        fetchFn: fetchEntity,
        fetchData: {
            entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
            entityId: warehouseReportId,
            entityScope: ENTITY_SCOPE.FULL,
        },
    });

    let { serviceData: warehouseReturnActionService, fetchServiceData: performWarehouseReturnAction } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: performEntityAction,
    });

    useModalServiceTriggers(warehouseReturnActionService, {
        ...props,
        errorTitle: "Failed to handle Warehouse Report",
        successTitle: "Successfully handled Warehouse Report",
    });

    const warehouseReturn = warehouseReturnService?.data;
    const warehouseItems = warehouseReturn?.items.filter((warehouseReturnItem) => warehouseReturnItem?.status !== WAREHOUSE_ITEM_STATUS.IGNORE);
    const itemsWithAction = warehouseItems?.filter((warehouseReturnItem) => warehouseReturnItem?.warehouseItemAction === WAREHOUSE_ITEM_ACTION.AWAITING_MANUAL_CHECK);

    const onSaveClick = useCallback(() => {
        if (warehouseItemUpdates?.length !== itemsWithAction?.length) {
            appProps.openSnackbarError({
                title: "You need to make an action choice for every item before submitting",
            });
        } else {
            performWarehouseReturnAction({
                entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
                entity: warehouseReturn,
                action: {
                    type: ENTITY_ACTION_TYPE.HANDLE_WAREHOUSE_COMPLAINT,
                    data: {
                        warehouseItemActions: warehouseItemUpdates?.map(({ id, approved }) => ({
                            id,
                            action: approved ? WAREHOUSE_ITEM_ACTION.WAREHOUSE_APPROVED : WAREHOUSE_ITEM_ACTION.WAREHOUSE_DENIED,
                        })),
                    },
                },
            });
            closeOnSubmit && onClose();
            onSubmit && onSubmit();
        }
    }, [warehouseReturn, warehouseItemUpdates, itemsWithAction, appProps.openSnackbarError, onSubmit, closeOnSubmit]);

    const loading = serviceIsLoading(warehouseReturnService);

    return (
        <TurnrModalContainer title={title} onClose={onClose}>
            <div style={{ maxWidth: "1000px" }}>
                <TurnrModalTitle>Handle warehouse report</TurnrModalTitle>
                <TurnrModalContent>
                    <p style={{ fontSize: "12px" }}>These items received remarks at the warehouse and need to be handled before proceeding</p>
                </TurnrModalContent>

                <TurnrCard loading={loading} LoaderComponent={SkeletonLoaderCardSlim} loadingMinHeight={"80px"} />
                {!loading && (
                    <TurnrTableSimple>
                        <TurnrTableHead>
                            <TurnrTableHeaderRow>
                                <TurnrTableHeaderCell>Item</TurnrTableHeaderCell>
                                <TurnrTableHeaderCell>Remark</TurnrTableHeaderCell>
                                <TurnrTableHeaderCell>Action</TurnrTableHeaderCell>
                            </TurnrTableHeaderRow>
                        </TurnrTableHead>
                        <TurnrTableBody>
                            {warehouseItems?.map((item) => {
                                let itemAction = item?.warehouseItemAction;
                                let warehouseItemId = item?.id;
                                return (
                                    <TurnrTableRow key={item?.id}>
                                        <TurnrTableCell>
                                            <AvatarAndItemName orderItem={item?.orderItem} includeItemId />
                                        </TurnrTableCell>
                                        <TurnrTableCell>{item?.image && <WarehouseComplaintInfo appProps={appProps} imgWidth={60} />}</TurnrTableCell>
                                        <TurnrTableCell>
                                            {itemAction === WAREHOUSE_ITEM_ACTION.AWAITING_MANUAL_CHECK ? (
                                                <AcceptDenyButtons
                                                    itemData={getItem(warehouseItemId)}
                                                    approveLabel={"Refund"}
                                                    disabled={serviceIsLoading(warehouseReturnActionService)}
                                                    onApproveClick={() =>
                                                        updateItem(warehouseItemId, {
                                                            approved: true,
                                                        })
                                                    }
                                                    onDenyClick={() =>
                                                        updateItem(warehouseItemId, {
                                                            approved: false,
                                                        })
                                                    }
                                                    onCancelClick={() => removeItem(warehouseItemId)}
                                                />
                                            ) : (
                                                <WarehouseItemActionStatusChip status={itemAction} />
                                            )}
                                        </TurnrTableCell>
                                    </TurnrTableRow>
                                );
                            })}
                        </TurnrTableBody>
                    </TurnrTableSimple>
                )}
                <TurnrModalFooter onSubmit={onSaveClick} loading={serviceIsLoading(warehouseReturnActionService)} />
            </div>
        </TurnrModalContainer>
    );
}
